import type { Notification, NotificationFeed } from '@/types/domain'

/* The seam between the server's spelling of a notification and the client's.
 *
 * The bell has two mouths feeding one cache — `GET /api/notifications` and the
 * SSE stream — and both carry the same row out of `outbox_messages`. That row's
 * `kind` is the outbox TOPIC (`case.assigned`, `quote.sent`), not the small
 * vocabulary the panel renders, so the mapping has to happen on the way in from
 * BOTH of them. It used to live in useNotificationStream.ts alone, which was
 * correct only for as long as the feed query was reading a fixture that already
 * spoke the panel's vocabulary: the moment the real endpoint replaced it, an
 * unmapped `case.assigned` reached `KIND_ICONS[kind]` as `undefined` and the
 * drawer crashed on the first real row rather than rendering it plainly.
 *
 * So the conversion lives here, once, and both readers go through it — the same
 * arrangement, and for the same reason, as `features/cases/api/caseWire.ts`.
 */

/** One bell event as the server spells it (`collab.NotificationDTO`).
 *
 *  Identical to `Notification` bar `kind`, which is a topic string here and a
 *  closed union there. Declared as `Omit<…>` rather than restated so a field
 *  added to the domain type cannot be silently dropped on the way in. */
export interface NotificationWire extends Omit<Notification, 'kind'> {
  kind: string
}

/** The feed and its badge (`collab.FeedDTO`). The count is the server's, taken
 *  verbatim and never recomputed here.
 *
 *  It is counted over the same rows `items` carries — the reachable ones, after
 *  the server's read-time access re-check — so it is capped at the server's
 *  `FeedLimit` (100) exactly as the list is, and a reader with four hundred
 *  unread sees 100. Recounting here would agree with it today, which is the
 *  trap: the day the feed is paged, a client counting the first page starts
 *  under-reporting silently instead of failing. */
export interface NotificationFeedWire {
  items: NotificationWire[]
  unreadCount: number
}

/** Maps an outbox topic to the icon vocabulary the panel renders.
 *
 *  A topic is a CONTRACT (AD-22): the server sends it verbatim, the client maps
 *  it to an icon and an i18n key, and renaming one is a breaking change rather
 *  than a refactor. The panel's own vocabulary is smaller and is about how a row
 *  LOOKS. Mapping here rather than widening `NotificationKind` keeps that union
 *  closed, so a component switching on it still gets exhaustiveness from
 *  TypeScript, and so a topic the server invents tomorrow needs no type change
 *  to be displayable.
 *
 *  An unknown topic falls back to 'status' rather than being dropped. A new
 *  server event appearing as a generic row is a cosmetic gap; a new server event
 *  vanishing from the bell is somebody never learning they were assigned. */
export function kindForTopic(topic: string): Notification['kind'] {
  switch (topic) {
    /* 'quote.participant-added' shares this arm: a party joining a quote reads
       as an assignment rather than a status, because the row says somebody was
       PUT ON a record, which is the same shape as being assigned to a case even
       though the party is external and the reader is the quote's owner. Letting
       it reach 'status' via the default would work, but the icon would say
       "something changed" about an event that is specifically about a person.

       'case.participant-assigned' is the email-channel twin of that (FR-48) and
       lands on the same arm: the bell never shows it today — the recipient is an
       external party with no login — but the stream and the feed are one code
       path, and an arm that is right if the row ever arrives costs nothing.

       'quote.team-assigned' is the internal half of that: a colleague was put
       on a quote, which is the same row shape as being assigned to a case and
       differs only in which record it names. Note what does NOT share this arm
       — 'quote.pricing-requested' below, which is the same act with the other
       meaning.

       The note sits above all four labels rather than between them: a comment
       BETWEEN two case labels makes the first one non-empty to no-fallthrough,
       which then reads a deliberate shared arm as a missing `break`. */
    case 'case.assigned':
    case 'case.participant-assigned':
    case 'quote.participant-added':
    case 'quote.team-assigned':
      return 'assignment'
    case 'case.stuck':
      return 'stuck'
    /* Chat, on either record kind. `quote.message` was missing here while the
       feed was a fixture and nothing noticed: it fell through to 'status', so a
       client's reply on a quote arrived under a tick rather than a speech
       bubble. */
    case 'case.message':
    case 'quote.message':
      return 'message'
    case 'case.file':
      return 'file'
    /* Money that moved on a case. An expense line recorded, amended or dropped
       is the case's cost changing, so it reads as a wallet rather than as a
       generic status tick — the reader's question about this row is "what did
       it cost", not "where is it". */
    case 'case.expense-changed':
      return 'payment'
    case 'finance.payment':
      return 'payment'
    /* A decision asked for, and a decision given. A rate change is money, but
       the SHAPE of both rows is an approval — somebody is being asked, or has
       answered — which is what the gavel says and the wallet does not. The
       client's answer on a quote is the same shape from outside the company.

       'quote.pricing-requested' joins them on that reading and NOT the
       assignment arm above, even though the server publishes it from the same
       staffing write. Both arms are defensible in the abstract; only one of
       them keeps the topic worth having. The server minted it as a separate
       topic precisely so a reader can tell "you were added to this quote" from
       "somebody is waiting for your price" (quote/team.go says so where the
       constant is declared), and mapping it to 'assignment' would render it
       under the same UserPlus as 'quote.team-assigned' — spending a wire
       contract on a distinction the bell then throws away. The gavel is the
       icon for a row that is asking the reader for something, which is exactly
       what this row does; the wallet is wrong because no money has moved yet,
       and the tick is wrong because nothing has changed state. */
    case 'quote.pricing-requested':
    case 'quote.answered':
    case 'quote.approval':
    case 'finance.approval':
    case 'finance.rate-change-requested':
    case 'finance.rate-change-decided':
      return 'approval'
    /* Something left the building as a document: the offer to the client, and
       whatever the document module issues. */
    case 'quote.sent':
    case 'document.issued':
      return 'document'
    /* The remaining contract topics — 'case.status', 'case.status-sent',
       'quote.converted' — are genuine status transitions and are served by the
       default arm. They are named here rather than given empty arms of their
       own: an arm returning what the default already returns is a line that can
       drift from its own comment. */
    default:
      return 'status'
  }
}

/** One event, server spelling to client spelling. */
export function notificationFromWire(wire: NotificationWire): Notification {
  return { ...wire, kind: kindForTopic(wire.kind) }
}

/** Whether a decoded value is shaped like a bell event at all.
 *
 *  `JSON.parse` guarantees syntax and nothing else: `{"foo":1}` parses happily
 *  and a cast to `NotificationWire` makes TypeScript believe it. What reaches
 *  the cache then is a row whose `id` and `href` are `undefined`, and the drawer
 *  renders `<Link to={undefined}>`, which react-router throws on — a malformed
 *  frame from the server takes the whole panel down rather than being ignored.
 *
 *  Two fields are checked and no more, deliberately. `id` is what the merge
 *  deduplicates on, so a row without one is a row that multiplies on every
 *  redelivery; `href` is what the panel navigates by and is the field that
 *  actually crashes. Everything else renders as an empty string, which is a
 *  cosmetic gap in one row rather than a broken bell — and a full validator here
 *  would be a second copy of the server's payload contract, drifting quietly. */
export function isNotificationWire(value: unknown): value is NotificationWire {
  if (typeof value !== 'object' || value === null) return false

  const row = value as Record<string, unknown>
  return typeof row.id === 'string' && row.id !== '' && typeof row.href === 'string'
}

/** The whole feed, server spelling to client spelling. */
export function feedFromWire(wire: NotificationFeedWire): NotificationFeed {
  return {
    items: wire.items.map(notificationFromWire),
    unreadCount: wire.unreadCount,
  }
}
